// shared-components/tooltip/tooltip.directive.ts
// LM shared tooltip directive — mirrors the GL implementation so LM
// components can use [lmTooltip] without coupling to the GL module.
// Visual styles are identical to GL (.ers-tooltip CSS) and are made
// available by importing tooltip.styles.scss in ers-leave-management-styles.scss.

import {
  Directive,
  input,
  effect,
  ElementRef,
  OnDestroy,
  Renderer2,
  HostListener,
} from '@angular/core';

@Directive({
  selector: '[lmTooltip]',
  standalone: true,
})
export class LmTooltipDirective implements OnDestroy {
  tooltipText = input('', { alias: 'lmTooltip' });
  tooltipPosition = input<'top' | 'bottom' | 'left' | 'right'>('bottom');
  tooltipDelay = input(300);
  tooltipDisabled = input(false);

  private tooltipElement: HTMLElement | null = null;
  private showTimeout: ReturnType<typeof setTimeout> | null = null;
  private isVisible = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
  ) {
    effect(() => {
      const text = this.tooltipText();
      if (this.tooltipElement) {
        const textSpan = this.tooltipElement.querySelector('.ers-tooltip-text');
        if (textSpan) {
          textSpan.textContent = text;
        }
      }
    });
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.tooltipDisabled() || !this.tooltipText()) return;
    this.showTimeout = setTimeout(() => this.show(), this.tooltipDelay());
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hide();
  }

  @HostListener('click')
  onClick(): void {
    this.hide();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(): void {
    if (this.isVisible) {
      this.updatePosition();
    }
  }

  private show(): void {
    if (this.isVisible) return;
    this.createTooltip();
    this.isVisible = true;
  }

  private hide(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
    if (this.tooltipElement) {
      this.renderer.removeClass(this.tooltipElement, 'ers-tooltip-visible');
      setTimeout(() => {
        this.removeTooltip();
      }, 150);
    }
    this.isVisible = false;
  }

  private createTooltip(): void {
    this.removeTooltip();

    this.tooltipElement = this.renderer.createElement('div');
    this.renderer.addClass(this.tooltipElement, 'ers-tooltip');
    this.renderer.addClass(this.tooltipElement, `ers-tooltip-${this.tooltipPosition()}`);

    const textSpan = this.renderer.createElement('span');
    this.renderer.addClass(textSpan, 'ers-tooltip-text');
    const text = this.renderer.createText(this.tooltipText());
    this.renderer.appendChild(textSpan, text);
    this.renderer.appendChild(this.tooltipElement, textSpan);

    const arrow = this.renderer.createElement('div');
    this.renderer.addClass(arrow, 'ers-tooltip-arrow');
    this.renderer.appendChild(this.tooltipElement, arrow);

    this.renderer.appendChild(document.body, this.tooltipElement);
    this.updatePosition();

    requestAnimationFrame(() => {
      if (this.tooltipElement) {
        this.renderer.addClass(this.tooltipElement, 'ers-tooltip-visible');
      }
    });
  }

  private updatePosition(): void {
    if (!this.tooltipElement) return;

    const hostRect    = this.el.nativeElement.getBoundingClientRect();
    const tooltipRect = this.tooltipElement.getBoundingClientRect();
    const scrollX     = window.scrollX || window.pageXOffset;
    const scrollY     = window.scrollY || window.pageYOffset;
    const gap         = 8;

    let top  = 0;
    let left = 0;

    switch (this.tooltipPosition()) {
      case 'bottom':
        top  = hostRect.bottom + gap + scrollY;
        left = hostRect.left + scrollX;
        break;
      case 'top':
        top  = hostRect.top - tooltipRect.height - gap + scrollY;
        left = hostRect.left + (hostRect.width - tooltipRect.width) / 2 + scrollX;
        break;
      case 'left':
        top  = hostRect.top + (hostRect.height - tooltipRect.height) / 2 + scrollY;
        left = hostRect.left - tooltipRect.width - gap + scrollX;
        break;
      case 'right':
        top  = hostRect.top + (hostRect.height - tooltipRect.height) / 2 + scrollY;
        left = hostRect.right + gap + scrollX;
        break;
    }

    const vw = window.innerWidth;
    if (left + tooltipRect.width > vw + scrollX - 8) {
      left = vw + scrollX - tooltipRect.width - 8;
    }
    if (left < scrollX + 8) {
      left = scrollX + 8;
    }

    this.renderer.setStyle(this.tooltipElement, 'top',  `${top}px`);
    this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
  }

  private removeTooltip(): void {
    if (this.tooltipElement && this.tooltipElement.parentNode) {
      this.renderer.removeChild(document.body, this.tooltipElement);
    }
    this.tooltipElement = null;
  }

  ngOnDestroy(): void {
    this.hide();
    this.removeTooltip();
  }
}
